import parseTextFromHtml from '../helpers/parseText';
import generateGeneralIntroMessage from './generalIntroMessage';
import convert24to12 from '../helpers/timeTransform';

const _ = require('lodash');

// --------------------------------------------------------------- //
// Section below are functions for getting HIGH-LEVEL cruise data
// --------------------------------------------------------------- //

/**
 * Function returns high-level cruise data
 */
const getHighLevelCruiseData = (travelOption, selectedItineraryRow) => {
  // Prepare some data
  const balconyCostText = selectedItineraryRow.costBalconyFrom
    && selectedItineraryRow.costBalconyFrom.price !== 0
    ? `Balcony Cabin from $${selectedItineraryRow.costBalconyFrom.price} per person before taxes and fees,`
    : '';
  const insideCostText = selectedItineraryRow.costInsideFrom
    && selectedItineraryRow.costInsideFrom.price !== 0
    ? `Inside Cabin from $${selectedItineraryRow.costInsideFrom.price} per person before taxes and fees,`
    : '';
  const oceanviewCostTest = selectedItineraryRow.costOceanviewFrom
    && selectedItineraryRow.costOceanviewFrom.price !== 0
    ? `Oceanview Cabin from $${selectedItineraryRow.costOceanviewFrom.price} per person before taxes and fees,`
    : '';
  const suiteCostText = selectedItineraryRow.costSuiteFrom
    && selectedItineraryRow.costSuiteFrom.price !== 0
    ? `Suite Cabin from $${selectedItineraryRow.costSuiteFrom.price} per person before taxes and fees,`
    : '';

  const data = `
    Itinerary Id: ${selectedItineraryRow.itineraryId},
    Itinerary Title: ${travelOption.itineraryTitle},

    Cruise Line: ${travelOption.cruiseLine ? travelOption.cruiseLine.cruiseLineName : ''},
    Ship Name: ${travelOption.ship ? travelOption.ship.shipName : ''},
    Ship Capacity: ${travelOption.ship ? travelOption.ship.shipCapacity : ''},
    Ship Year Built: ${travelOption.ship ? travelOption.ship.shipYearBuilt : ''},

    Departure City: ${travelOption.itineraryDepartCity},
    Destination: ${travelOption.itineraryDestinationName},
    Ports of Call: ${travelOption.itineraryPortsOfCall},
    Itinerary Return City: ${travelOption.itineraryReturnCity},
    Departure Date: ${selectedItineraryRow.itineraryDepartDate},
    Departure Date: ${selectedItineraryRow.itineraryReturnDate},

    Below are the lowest possible per person price (before taxes and fees) for each stateroom type:
    ${balconyCostText}
    ${insideCostText}
    ${oceanviewCostTest}
    ${suiteCostText}
  `;

  return data;
};

// --------------------------------------------------------------- //
// Section below are functions to getting DETAILED cruise data
// --------------------------------------------------------------- //

export const getHighLevelData = (cruiseDetails) => {
  // Handle potential error
  if (!cruiseDetails) {
    return 'Highlevel cruise data are not available.\n';
  }
  const highlevelData = `
  General Information about the cruise itinerary
  Itinerary Id: ${cruiseDetails.itineraryId},
  Itinerary Title: ${cruiseDetails.itineraryTitle},
  Destination: ${cruiseDetails.itineraryDestinationName},
  Ports of Call: ${cruiseDetails.itineraryPortsOfCall},
  `;
  return highlevelData;
};

const getItineraryData = (cruiseDetails) => {
  // Handle potential error
  if (!cruiseDetails || !cruiseDetails.itineraryDays) {
    return {
      itineraryData: 'Itinerary data are are not available.\n',
      cityDescriptionData: 'City description data are not available.\n',
    };
  }
  // Day-to-day itinerary data
  const mapCityToDescription = {};
  let itineraryData = '\nDetailed day-to-day itinerary information\n';
  cruiseDetails.itineraryDays.forEach((day) => {
    /* For saving token, we try to dedup the day if arrival and departure share the
     * same one. We also try to convert convert the time to a simpler version */
    const dayArrive = day && day.itineraryDayArrivalDateTime ? day.itineraryDayArrivalDateTime.split('T')[0] : null;
    const dayDepart = day && day.itineraryDayDepartureDateTime ? day.itineraryDayDepartureDateTime.split('T')[0] : null;
    const theDay = dayArrive && dayDepart && dayArrive === dayDepart ? `, ${dayArrive}` : null;
    let arriveDatetime = '';
    let departDatetime = '';
    if (theDay) {
      arriveDatetime = `, arrival ${convert24to12(day.itineraryDayArrivalDateTime.split('T')[1])}`;
      departDatetime = `, departure ${convert24to12(day.itineraryDayDepartureDateTime.split('T')[1])}`;
    } else {
      arriveDatetime = day.itineraryDayArrivalDateTime
        ? `, arrival ${day.itineraryDayArrivalDateTime}`
        : '';
      departDatetime = day.itineraryDayDepartureDateTime
        ? `, departure ${day.itineraryDayDepartureDateTime}`
        : '';
    }

    // Append the reformatted day-to-day itinerary data
    itineraryData += `Day${day.itineraryDayNumber}, `;
    itineraryData += day.cityName;
    itineraryData += theDay || '';
    itineraryData += arriveDatetime;
    itineraryData += departDatetime;
    itineraryData += '.\n';

    // Record the unique description of the city
    if (!mapCityToDescription[day.cityName]) {
      mapCityToDescription[day.cityName] = day.cityDescription;
    }
  });
  // Description of each city/location in the itinerary
  let cityDescriptionData = '\nDescription of each city/location in the itinerary\n';
  Object.entries(mapCityToDescription).forEach(([city, description]) => {
    cityDescriptionData += `${city}: ${parseTextFromHtml(description)}\n`;
  });
  return { itineraryData, cityDescriptionData };
};

const getCruiseLineData = (cruiseDetails) => {
  // Handle potential error
  if (!cruiseDetails || !cruiseDetails.cruiseLine) {
    return {
      CruiselineData: 'Cruise line data are are not available.\n',
      detailedCruiselineData: 'Detailed cruise line data are not available.\n',
    };
  }
  // Cruiseline data
  const CruiselineData = `Cruiseline Name: ${cruiseDetails.cruiseLine.cruiseLineName},`;
  const detailedCruiselineData = `
      Cruiseline Description: ${parseTextFromHtml(cruiseDetails.cruiseLine.cruiseLineDescription)},\n,`;
  return { CruiselineData, detailedCruiselineData };
};

const getShipData = (cruiseDetails) => {
  // Handle potential error
  if (!cruiseDetails || !cruiseDetails.ship) {
    return {
      shipData: 'Ship data are are not available.\n',
      shipAmenitiesData: 'Ship amenities data are not available.\n',
      detailedShipData: 'Detailed ship data are not available.\n',
    };
  }
  // Ship data, the amenities and data about the ship
  const shipData = `Ship Name: ${cruiseDetails.ship.shipName}
    Ship Year Built: ${cruiseDetails.ship.shipYearBuilt}
    Ship Tonnage: ${cruiseDetails.ship.shipTonnage}
  `;
  const shipAmenitiesData = `
    Ship Amenities: [
      Restaurants: ${cruiseDetails.ship.shipNumRestaurants},
      Bars: ${cruiseDetails.ship.shipNumBars},
      Pools: ${cruiseDetails.ship.shipNumPools},
      Theaters: ${cruiseDetails.ship.shipNumTheaters},
      Meeting Rooms: ${cruiseDetails.ship.shipHasMeetingRooms},
      Casino: ${cruiseDetails.ship.shipHasCasino},
      Disco: ${cruiseDetails.ship.shipHasDisco},
      Fitness Center: ${cruiseDetails.ship.shipHasFitnessCenter},
      Childrens Program: ${cruiseDetails.ship.shipHasChildrensProgram},
      Internet Cafe: ${cruiseDetails.ship.shipHasInternetCafe},
      Shops: ${cruiseDetails.ship.shipHasShops},
      Library: ${cruiseDetails.ship.shipHasLibrary},
      Spa: ${cruiseDetails.ship.shipHasSpa},
    ]\n`.replace(/\s+/g, ' ').trim();
  const detailedShipData = `Ship Description: ${parseTextFromHtml(cruiseDetails.ship.shipDescription)},\n`;
  return { shipData, shipAmenitiesData, detailedShipData };
};

const getStateroomsData = (cruiseRatesArray) => {
  // Handle potential error
  if (!cruiseRatesArray) {
    return {
      stateroomsData: 'Staterooms data are not available.\n',
      detailedStateroomsData: 'Detailed staterooms data are not available.\n',
    };
  }

  // Initialize variables for getting staterooms data
  let stateroomsData = '';
  let stateroomNum = 1;
  const cabinCategoryDescriptionMap = {}; // We share the map across staterooms
  if (cruiseRatesArray) {
    cruiseRatesArray.forEach((stateroomRates) => {
      stateroomsData += `\n----BEGIN SET ${stateroomNum}----\n`;

      // Get data for each cabin category and option
      const allRateOptions = [];
      if (stateroomRates.cabinCategories) {
        stateroomRates.cabinCategories.forEach((cabinCategory) => {
          /* We grouped cabin categories with the same description in "cabinCategoryDescriptionMap".
           * Note: [1] Assumed the same cabinCategoryName shares the same description [2] In the
           * data, there are many line with same cabinCategoryName but different price due to
           * rate option. [3] Different cabinCategoryName might share the same description */
          const description = parseTextFromHtml(cabinCategory.cabinCategoryDescription);
          if (!cabinCategoryDescriptionMap[description]) {
            cabinCategoryDescriptionMap[description] = [];
          }
          if (!cabinCategoryDescriptionMap[description].includes(cabinCategory.cabinCategoryName)) {
            cabinCategoryDescriptionMap[description].push(cabinCategory.cabinCategoryName);
          }

          // Collect rate options data to an array for sorting later
          if (cabinCategory.rateOptions) {
            cabinCategory.rateOptions.forEach((rateOption) => {
              // eslint-disable-next-line no-param-reassign
              rateOption.cabinCategoryName = cabinCategory.cabinCategoryName;
              // eslint-disable-next-line no-param-reassign
              rateOption.cabinCategoryLocation = cabinCategory.cabinCategoryLocation;
              allRateOptions.push(rateOption);
            });
          }
        });
      }

      // Fill the sorted rate options data
      const rateOptionsSet = new Set(); // We have unique set for each stateroom
      const sortedRateOptions = allRateOptions.sort((a, b) => a.price - b.price);
      if (sortedRateOptions) {
        sortedRateOptions.forEach((rateOption) => {
          /* Within each cabin category, skip the current rate option if the set contains it.
           * In the data, within a same cabin category, there might be same rate option with
           * same price, so we can dedup them. (Not necessarily need 'rateOptionType') */
          const rateOptionKey = `${rateOption.cabinCategoryName} - ${rateOption.rateOptionName} - ${rateOption.rateOptionType} - ${rateOption.price}`;
          if (rateOptionsSet.has(rateOptionKey)) return;
          rateOptionsSet.add(rateOptionKey);

          /* Add the current rate option to the set and the text.
           * Regards no refund info as nonRefundable, or NLP model can easily run into error */
          const refundableText = (!rateOption.refundableStatusFromRules
            || rateOption.refundableStatusFromRules === 'noInfo'
            || rateOption.refundableStatusFromRules === 'nonRefundable')
            ? ' deposit non-refundable'
            : ' deposit refundable';
          stateroomsData += `${rateOption.cabinCategoryName} - ${rateOption.rateOptionName}.`;
          stateroomsData += `$${rateOption.price}.`;
          stateroomsData += `It has ${rateOption.cabinCategoryLocation}.`;
          stateroomsData += `${refundableText}\n`;
        });
      }

      // Handle the case when there is no cabin data
      if (!stateroomRates.cabinCategories || stateroomRates.cabinCategories.length === 0) {
        stateroomsData += 'No cabin data available for this stateroom.\n';
      }

      stateroomsData += `\n----END SET ${stateroomNum}----\n`;
      stateroomNum += 1;
    });
  }

  // Fill the detailed cabin category information
  let detailedStateroomsData = '';
  Object.entries(cabinCategoryDescriptionMap).forEach(([description, cabinCategoryArr]) => {
    detailedStateroomsData += 'Description about cabin categories';
    for (let i = 0; i < cabinCategoryArr.length; i += 1) {
      detailedStateroomsData += ` '${cabinCategoryArr[i]}'`;
      if (i !== cabinCategoryArr.length - 1) detailedStateroomsData += ', ';
      else detailedStateroomsData += ':\n';
    }
    detailedStateroomsData += `${description}\n\n`;
  });

  return { stateroomsData, detailedStateroomsData };
};

/**
 * Function returns detailed cruise data
 */
const getDetailedCruiseData = (cruiseDetails, cruiseRatesArray) => {
  // Getting all the data
  const highlevelData = getHighLevelData(cruiseDetails);
  const { itineraryData, cityDescriptionData } = getItineraryData(cruiseDetails);
  const { CruiselineData, detailedCruiselineData } = getCruiseLineData(cruiseDetails);
  const { shipData, shipAmenitiesData, detailedShipData } = getShipData(cruiseDetails);
  const { stateroomsData, detailedStateroomsData } = getStateroomsData(cruiseRatesArray);

  // Turn into text
  const data = `
    ${highlevelData}
    ${CruiselineData}
    ${shipData}
    ${shipAmenitiesData}
    ${itineraryData}
    ${stateroomsData}

    ${detailedStateroomsData}
    ${detailedShipData}
    ${detailedCruiselineData}
    ${cityDescriptionData}
  `;
  return data;
};

// --------------------------------------------------------------- //
// Section below are cruise's instructions generating functions
// --------------------------------------------------------------- //

/**
 * A fast generated introductory message for cruise before the time-consuming
 * data fetching are completed
 */
const generateCruiseGeneralIntroMessage = (
  travelOption,
  selectedItineraryRow,
  nlpConstants,
  nlpInstructions,
) => {
  const generalIntro = generateGeneralIntroMessage(nlpConstants, nlpInstructions);

  // Handle potential error
  if (!travelOption || !selectedItineraryRow) {
    return generalIntro;
  }
  // Get high-level cruise data
  const highLevelCruiseData = getHighLevelCruiseData(travelOption, selectedItineraryRow);
  return highLevelCruiseData;
};

/**
 * Function generates introductory message for cruise.
 */
const generateCruiseIntroMessage = (
  cruiseDetails,
  cruiseRatesArray,
) => {
  // Get local copy of parameters to prevent unwanted mutation
  const localCruiseDetails = _.cloneDeep(cruiseDetails);
  const localCruiseRatesArray = _.cloneDeep(cruiseRatesArray);

  // Get detailed cruise data
  const detailedCruiseData = getDetailedCruiseData(localCruiseDetails, localCruiseRatesArray);
  return detailedCruiseData;
};

export { generateCruiseIntroMessage, generateCruiseGeneralIntroMessage, getDetailedCruiseData };
